/**
 * Admin (spec page 10, approved): only two things are editable in the app -
 *   (a) people + roles: editor / division head / staff, and each person's division
 *   (b) capacity + scoring: hours per day/week, tier thresholds
 * Divisions, the org tree, stages and types live in config.ts.
 * People appear here after their first sign-in (functions/src/people.ts).
 */
import { useEffect, useState } from 'react';
import { DIVISIONS, DEFAULT_SETTINGS, OWNER_UID, type DivisionId, type Settings } from '../config';
import { deletePerson, saveSettings, savePerson, setCollectionsAccess, usePeople, useRoles, type Me, type Person } from '../lib/data';
import { Empty } from '../ui';
import { Guide } from '../guide';

export function Admin({ me, settings }: { me: Me; settings: Settings }) {
  return (
    <div className="admin">
      <Guide page="admin" />
      <People me={me} />
      <Capacity settings={settings} />
    </div>
  );
}

const OWNER_TIP = 'Owner - cannot be changed';

function People({ me }: { me: Me }) {
  const people = usePeople(me);
  const roles = useRoles(me);
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const shown = people.filter((p) => !q || `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase()));
  const unassigned = people.filter((p) => p.division === 'all').length;

  async function change(p: Person, division: DivisionId, role: Person['role']) {
    setMsg(null);
    if (p.uid === OWNER_UID) { setMsg({ ok: false, text: `${OWNER_TIP}.` }); return; }
    if (p.uid === me.uid && role !== 'editor') { setMsg({ ok: false, text: "You can't remove your own editor access." }); return; }
    if (role === 'divisionHead' && division === 'all') { setMsg({ ok: false, text: 'Pick a division before making someone a division head.' }); return; }
    try { await savePerson(roles, p, division, role); setMsg({ ok: true, text: `Saved ${p.name}.` }); }
    catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }

  async function toggleCollections(p: Person, on: boolean) {
    setMsg(null);
    try { await setCollectionsAccess(p.uid, on); setMsg({ ok: true, text: `${on ? 'Gave' : 'Removed'} Collections access ${on ? 'to' : 'for'} ${p.name}.` }); }
    catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }

  async function remove(p: Person) {
    setMsg(null); setConfirm(null);
    try { await deletePerson(me, p); setMsg({ ok: true, text: `Deleted ${p.name}.` }); }
    catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }

  return (
    <section className="card">
      <div className="toolbar">
        <h2>People + roles <span className="count">{people.length}</span></h2>
        {unassigned > 0 && <span className="hint">{unassigned} without a division</span>}
        <span className="spacer" />
        <input className="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person..." />
      </div>
      <p className="muted small">
        People show up here after they sign in once. <b>Editors</b> (you, Robin) see and change everything.
        <b> Division heads</b> will see their division's requests (phase 2). <b>Staff</b> submit and track their own.
        <b> Collections</b> is a separate on/off switch: only people switched on see the Collections tab, editors included.
        A person's division pre-fills their request form. <b>Delete</b> removes someone from this list and
        takes away any editor or division-head access; their requests stay. If they sign in again they come back as Staff.
      </p>
      {msg && <p className={msg.ok ? 'ok' : 'err'}>{msg.text}</p>}
      {!shown.length && <Empty>No one has signed in yet.</Empty>}
      <table className="list admin-table">
        <thead><tr><th>Name</th><th>Email</th><th>Division</th><th>Role</th><th>Collections</th><th></th></tr></thead>
        <tbody>
          {shown.map((p) => (
            <tr key={p.uid} className="no-click">
              <td className="t">{p.name}{p.uid === me.uid && <span className="muted small"> (you)</span>}
                {p.uid === OWNER_UID && <span className="badge owner-badge" title={OWNER_TIP}> Owner</span>}</td>
              <td className="muted">{p.email}</td>
              <td>
                <select value={p.division} disabled={p.uid === OWNER_UID} title={p.uid === OWNER_UID ? OWNER_TIP : undefined} onChange={(e) => change(p, e.target.value as DivisionId, p.role)}>
                  {DIVISIONS.map((d) => <option key={d.id} value={d.id}>{d.id === 'all' ? 'Not assigned' : d.label}</option>)}
                </select>
              </td>
              <td>
                <select value={p.role} disabled={p.uid === OWNER_UID} title={p.uid === OWNER_UID ? OWNER_TIP : undefined} onChange={(e) => change(p, p.division, e.target.value as Person['role'])}>
                  <option value="staff">Staff</option>
                  <option value="divisionHead">Division head</option>
                  <option value="editor">Editor</option>
                </select>
              </td>
              <td>
                {(() => {
                  const locked = p.uid === OWNER_UID;
                  const on = locked || roles.collections.includes(p.uid);
                  return (
                    <button type="button" role="switch" aria-checked={on} aria-label={`Collections access for ${p.name}`}
                      disabled={locked} title={locked ? OWNER_TIP : undefined}
                      className={on ? '' : 'secondary'} onClick={() => toggleCollections(p, !on)}>{on ? 'On' : 'Off'}</button>
                  );
                })()}
              </td>
              <td>
                {p.uid === OWNER_UID ? (
                  <button type="button" className="linkish" disabled title={OWNER_TIP} aria-label={`Delete ${p.name} (${OWNER_TIP})`}>Delete</button>
                ) : p.uid === me.uid ? null : confirm === p.uid ? (
                  <>
                    <button className="danger" onClick={() => remove(p)}>Confirm delete</button>{' '}
                    <button className="linkish" onClick={() => setConfirm(null)}>cancel</button>
                  </>
                ) : (
                  <button className="linkish danger-text" onClick={() => setConfirm(p.uid)}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function Capacity({ settings }: { settings: Settings }) {
  const [s, setS] = useState<Settings>(settings);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => setS(settings), [settings]);
  const t = s.tiers;
  const valid = s.capacityDay > 0 && s.capacityDay <= 24 && s.capacityWeek > 0 && s.capacityWeek <= 168
    && Number.isInteger(t.critical) && Number.isInteger(t.high) && Number.isInteger(t.medium)
    && t.critical > t.high && t.high > t.medium && t.medium >= 1 && t.critical <= 25;

  async function save() {
    setMsg(null);
    try { await saveSettings(s); setMsg({ ok: true, text: 'Saved.' }); } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }
  const num = (v: string) => (v === '' ? 0 : Number(v));

  return (
    <section className="card">
      <h2>Capacity + scoring</h2>
      <div className="settings-grid">
        <label>Hours per day <input className="num-input" inputMode="decimal" value={s.capacityDay} onChange={(e) => setS({ ...s, capacityDay: num(e.target.value) })} /></label>
        <label>Hours per week <input className="num-input" inputMode="decimal" value={s.capacityWeek} onChange={(e) => setS({ ...s, capacityWeek: num(e.target.value) })} /></label>
        <div className="muted small span2">Planner lists and the Lineup's capacity line use these.</div>
        <label>Critical at score &ge; <input className="num-input" value={t.critical} onChange={(e) => setS({ ...s, tiers: { ...t, critical: num(e.target.value) } })} /></label>
        <label>High at &ge; <input className="num-input" value={t.high} onChange={(e) => setS({ ...s, tiers: { ...t, high: num(e.target.value) } })} /></label>
        <label>Medium at &ge; <input className="num-input" value={t.medium} onChange={(e) => setS({ ...s, tiers: { ...t, medium: num(e.target.value) } })} /></label>
        <div className="muted small span2">Score = Impact (1-5) x Urgency (1-5), so 1 to 25. Anything below Medium is Low.</div>
      </div>
      <div className="actions">
        <button className="secondary" onClick={() => setS(DEFAULT_SETTINGS)}>Reset to defaults</button>
        <button onClick={save} disabled={!valid}>Save</button>
      </div>
      {!valid && <p className="err">Check the numbers: tiers must go Critical &gt; High &gt; Medium, whole numbers, max 25.</p>}
      {msg && <p className={msg.ok ? 'ok' : 'err'}>{msg.text}</p>}
    </section>
  );
}
